import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, ClipboardList, Lock, Unlock, AlertTriangle, BarChart3, RefreshCw,
  FileText, Archive, ArchiveRestore,
} from 'lucide-react';
import {
  useAcquireLock,
  useAuditDetail,
  useAuditEvidence,
  useAuditResponses,
  useAuditTemplate,
  useCurrentUser,
  useHeartbeat,
  useLockState,
  useReleaseLock,
  useUnarchiveAudit,
  lockConflictHolder,
} from '../../lib/api/queries';
import { ARCHIVE_REASON_LABELS } from '../../lib/api/types';
import type { ArchiveReason, LockState } from '../../lib/api/types';
import { AuditMetadataHeader } from './AuditMetadataHeader';
import { SectionSidebar } from './SectionSidebar';
import { CriterionCard } from './CriterionCard';
import { ScorecardView } from './ScorecardView';
import { BreakdownView } from './BreakdownView';
import { ReportView } from './report/ReportView';

type Tab = 'form' | 'results' | 'report';

const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;

export function AuditDetailPage() {
  const { id: auditId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: user } = useCurrentUser();
  const tenantId = user?.tenantId ?? '';
  const userId   = user?.userId ?? '';

  // ---- Lock pieces (unchanged from prior slice) -----------------------
  const lockQuery   = useLockState(tenantId, auditId);
  const acquireLock = useAcquireLock(tenantId, auditId);
  const heartbeat   = useHeartbeat(tenantId, auditId);
  const releaseLock = useReleaseLock(tenantId, auditId);

  const [conflictHolder, setConflictHolder] = useState<LockState | null>(null);
  const [acquiring, setAcquiring] = useState(false);
  const acquireAttemptedRef = useRef<string | null>(null);

  const liveLock = lockQuery.data ?? null;
  const isHolder = !!liveLock && liveLock.lockedByUserId === userId;

  const tryAcquire = () => {
    setConflictHolder(null);
    setAcquiring(true);
    acquireLock.mutateAsync()
      .then(() => {})
      .catch((err) => {
        const holder = lockConflictHolder(err);
        if (holder) setConflictHolder(holder);
      })
      .finally(() => setAcquiring(false));
  };

  useEffect(() => {
    if (!auditId) return;
    if (acquireAttemptedRef.current === auditId) return;
    acquireAttemptedRef.current = auditId;
    tryAcquire();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId]);

  useEffect(() => {
    if (liveLock === null || (liveLock && liveLock.lockedByUserId === userId)) {
      setConflictHolder(null);
    }
  }, [liveLock, userId]);

  useEffect(() => {
    if (!isHolder) return;
    const id = setInterval(() => heartbeat.mutate(), HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHolder]);

  useEffect(() => {
    return () => {
      if (isHolder) releaseLock.mutate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId, isHolder]);

  // ---- Form data ------------------------------------------------------
  const auditQuery     = useAuditDetail(tenantId, auditId);
  const templateQuery  = useAuditTemplate(auditQuery.data?.templateVersionId);
  const responsesQuery = useAuditResponses(tenantId, auditId);
  const evidenceQuery  = useAuditEvidence(tenantId, auditId);

  const audit     = auditQuery.data;
  const template  = templateQuery.data;
  const responses = responsesQuery.data;
  const evidence  = evidenceQuery.data?.items;

  // Default to the first section once the template lands.
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  useEffect(() => {
    if (!activeSectionId && template && template.sections.length > 0) {
      setActiveSectionId(template.sections[0].id);
    }
  }, [activeSectionId, template]);

  const activeSection = useMemo(
    () => template?.sections.find(s => s.id === activeSectionId) ?? null,
    [template, activeSectionId],
  );

  const loading = auditQuery.isLoading || templateQuery.isLoading;

  // Slice 18b — an archived audit is read-only. The server enforces this on
  // every mutating route (RequireWritableAudit → 409), so this flag is purely
  // about not presenting controls that are guaranteed to fail.
  const archived = !!audit?.archivedAtUtc;
  const editable = isHolder && !!audit && !archived;

  const [activeTab, setActiveTab] = useState<Tab>('form');

  return (
    <div className="space-y-6">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-accent-alt-600 hover:text-accent-alt-700"
      >
        <ArrowLeft className="w-4 h-4" aria-hidden />
        Back to audits
      </Link>

      <header>
        <h1 className="text-2xl font-semibold text-accent-alt-700 break-all">
          {audit?.title ?? `Audit ${auditId.slice(0, 8)}…`}
        </h1>
        {template && (
          <p className="text-xs text-gray-500 mt-1">
            Template v{template.versionNumber} — {template.versionName}
          </p>
        )}
      </header>

      {archived && audit && (
        <ArchivedBanner
          tenantId={tenantId}
          auditId={auditId}
          archivedAtUtc={audit.archivedAtUtc!}
          reason={audit.archiveReason}
          note={audit.archiveNote}
        />
      )}

      {audit && (
        <AuditMetadataHeader tenantId={tenantId} audit={audit} editable={editable} />
      )}

      <LockBanner
        conflictHolder={conflictHolder}
        liveLock={liveLock}
        isHolder={isHolder}
        acquirePending={acquiring}
        onRetryAcquire={() => {
          acquireAttemptedRef.current = null;
          tryAcquire();
        }}
        onRelease={() => {
          releaseLock.mutate(undefined, {
            onSuccess: () => navigate('/'),
          });
        }}
      />

      {loading && (
        <div className="text-sm text-gray-500">Loading audit…</div>
      )}

      {!loading && template && (
        <TabBar activeTab={activeTab} onChange={setActiveTab} />
      )}

      {!loading && template && activeTab === 'results' && (
        <div className="space-y-6">
          <ScorecardView tenantId={tenantId} auditId={auditId} />
          <BreakdownView tenantId={tenantId} auditId={auditId} />
        </div>
      )}

      {!loading && template && activeTab === 'report' && (
        <ReportView tenantId={tenantId} auditId={auditId} editable={editable} />
      )}

      {!loading && template && activeTab === 'form' && (
        <div className="grid grid-cols-1 lg:grid-cols-[18rem_1fr] gap-6 items-start">
          <div className="lg:sticky lg:top-4">
            <SectionSidebar
              sections={template.sections}
              responses={responses}
              activeSectionId={activeSectionId}
              onSelect={setActiveSectionId}
            />
          </div>

          <div className="space-y-4">
            {activeSection ? (
              <>
                <SectionHeader sectionCode={activeSection.code} sectionName={activeSection.name} />
                {activeSection.criteria.map(c => (
                  <CriterionCard
                    key={c.id}
                    tenantId={tenantId}
                    auditId={auditId}
                    criterion={c}
                    responses={responses}
                    evidence={evidence}
                    editable={editable}
                  />
                ))}
              </>
            ) : (
              <p className="text-sm text-gray-500">Pick a section from the list to start.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TabBar({ activeTab, onChange }: { activeTab: Tab; onChange: (t: Tab) => void }) {
  const tabCls = (t: Tab) =>
    'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ' +
    (activeTab === t
      ? 'bg-accent-alt-600 text-white'
      : 'text-gray-700 hover:bg-gray-100');
  return (
    <div className="flex gap-1 border-b border-gray-200 pb-2">
      <button type="button" onClick={() => onChange('form')} className={tabCls('form')}>
        <ClipboardList className="w-4 h-4" aria-hidden /> Form
      </button>
      <button type="button" onClick={() => onChange('results')} className={tabCls('results')}>
        <BarChart3 className="w-4 h-4" aria-hidden /> Results
      </button>
      <button type="button" onClick={() => onChange('report')} className={tabCls('report')}>
        <FileText className="w-4 h-4" aria-hidden /> Report
      </button>
    </div>
  );
}

function SectionHeader({ sectionCode, sectionName }: { sectionCode: string; sectionName: string }) {
  return (
    <div className="bg-accent-alt-50 border border-accent-alt-100 rounded-lg px-5 py-3">
      <p className="text-xs font-semibold text-accent-alt-700 uppercase tracking-wide">
        Section {sectionCode}
      </p>
      <h2 className="text-lg font-semibold text-accent-alt-700">{sectionName}</h2>
    </div>
  );
}

function LockBanner({
  conflictHolder,
  liveLock,
  isHolder,
  acquirePending,
  onRetryAcquire,
  onRelease,
}: {
  conflictHolder: LockState | null;
  liveLock: LockState | null;
  isHolder: boolean;
  acquirePending: boolean;
  onRetryAcquire: () => void;
  onRelease: () => void;
}) {
  if (conflictHolder) {
    return (
      <div className="bg-danger-50 border border-danger-500/40 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-danger-500 shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1">
          <p className="font-medium text-danger-500">
            Locked by another user
          </p>
          <p className="text-sm text-gray-700 mt-1">
            Acquired {new Date(conflictHolder.acquiredAtUtc).toLocaleTimeString()},
            expires {new Date(conflictHolder.expiresAtUtc).toLocaleTimeString()}.
          </p>
          <button
            onClick={onRetryAcquire}
            className="mt-2 inline-flex items-center gap-1.5 text-sm text-accent-alt-600 hover:text-accent-alt-700"
          >
            <RefreshCw className="w-3.5 h-3.5" aria-hidden />
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (isHolder && liveLock) {
    return (
      <div className="bg-success-500/10 border border-success-500/40 rounded-lg p-4 flex items-start gap-3">
        <Lock className="w-5 h-5 text-success-500 shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1">
          <p className="font-medium text-success-500">You have the edit lock</p>
          <p className="text-sm text-gray-700 mt-1">
            Expires {new Date(liveLock.expiresAtUtc).toLocaleTimeString()}.
            Auto-renewed every 2 minutes while this page is open.
          </p>
        </div>
        <button
          onClick={onRelease}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
        >
          <Unlock className="w-3.5 h-3.5" aria-hidden />
          Release
        </button>
      </div>
    );
  }

  if (acquirePending) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        Acquiring lock…
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3">
      <Unlock className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" aria-hidden />
      <div className="flex-1">
        <p className="font-medium text-gray-700">Lock is available</p>
        <p className="text-sm text-gray-500 mt-1">
          No one is currently editing this audit. Acquire the lock to start.
        </p>
        <button
          onClick={onRetryAcquire}
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
        >
          <Lock className="w-3.5 h-3.5" aria-hidden />
          Acquire lock
        </button>
      </div>
    </div>
  );
}

/**
 * Shown in place of the editing affordances when an audit has been archived.
 * Deliberately offers the way out (unarchive) rather than only stating the
 * restriction — the most common reason to open an archived audit is that
 * something needs changing after all.
 *
 * Reads are unaffected: the report, its PDFs and the evidence all still load,
 * and any published report is still visible to the client.
 */
function ArchivedBanner({
  tenantId,
  auditId,
  archivedAtUtc,
  reason,
  note,
}: {
  tenantId: string;
  auditId: string;
  archivedAtUtc: string;
  reason: ArchiveReason | null;
  note: string | null;
}) {
  const unarchive = useUnarchiveAudit(tenantId);

  return (
    <div className="flex items-start gap-3 rounded-lg border border-gray-300 bg-gray-50 px-4 py-3">
      <Archive className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" aria-hidden />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">
          Archived {new Date(archivedAtUtc).toLocaleDateString()}
          {reason && <> — {ARCHIVE_REASON_LABELS[reason]}</>}
        </p>
        <p className="text-sm text-gray-600">
          This audit is read-only. Unarchive it to make changes.
        </p>
        {note && <p className="text-sm text-gray-500 mt-1 italic">{note}</p>}
      </div>
      <button
        type="button"
        onClick={() => unarchive.mutate({ auditId })}
        disabled={unarchive.isPending}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                   text-accent-alt-700 hover:bg-accent-alt-50 rounded-md disabled:opacity-50"
      >
        <ArchiveRestore className="w-4 h-4" aria-hidden />
        {unarchive.isPending ? 'Unarchiving…' : 'Unarchive'}
      </button>
    </div>
  );
}
